import LPCCompositor from '../character/LPCCompositor.js';
import {
  BODY_TYPE_LIST, CREATOR_CATEGORIES, SKIN_SWATCHES, HAIR_SWATCHES, EYE_SWATCHES,
  VARIANT_SWATCHES, buildLayerStack, getItemsForCategory, getVariantsForItem,
  getSkinVariants, isItemCompatibleWithBody, loadManifest, findItemById, getFileIndex
} from '../character/LPCLayerDefinitions.js';
import { generateRandomAppearance } from '../character/LPCRandomAppearance.js';

/**
 * CharacterCreator - In-game character appearance editor
 *
 * Uses LPC sprite layers with a manifest-driven tabbed UI for full
 * character customization: body, face, hair, features, extras.
 * Generates a live preview via LPCCompositor.
 */

// Body type → body item overrides (skeleton/zombie use different body items)
const BODY_ITEM_OVERRIDES = {
  'skeleton-male': { item: 'body_skeleton', variant: 'skeleton' },
  'zombie-male':   { item: 'body_zombie',   variant: 'zombie' }
};

// Per-category crop regions for thumbnail close-ups.
// { sx, sy, sw, sh } defines source rect from the full 64×64 frame.
// null = show full body.
const HEAD_CROP = { sx: 10, sy: 4, sw: 44, sh: 44 };
const EYES_CROP = { sx: 14, sy: 10, sw: 36, sh: 36 };
const THUMB_CROPS = {
  head: HEAD_CROP, face: HEAD_CROP, eyes: EYES_CROP,
  eyebrows: EYES_CROP, hair: { sx: 6, sy: 0, sw: 52, sh: 44 },
  hairext: { sx: 6, sy: 0, sw: 52, sh: 44 },
  beard: { sx: 10, sy: 12, sw: 44, sh: 44 }, ears: HEAD_CROP,
  nose: EYES_CROP, facial: HEAD_CROP, horns: { sx: 6, sy: 0, sw: 52, sh: 52 },
  tail: null, wings: null
};

// LPC direction row offsets: north=0, west=64, south=128, east=192
const DIR_SOUTH = 128;
const DIR_NORTH = 0;
// Body base zPos — layers below this are behind the body and hidden from front view
const BODY_ZPOS = 10;

// ── Thumbnail Renderer ───────────────────────────────────────────────
// Generates 64×64 preview thumbnails by extracting idle frames from LPC
// sprite sheets and compositing on a body+head base.

class ThumbnailRenderer {
  constructor() {
    this._bases = {};          // direction → { canvas, key }
    this._cache = new Map();   // "itemId:variant:dir:baseKey" → canvas
  }

  async renderBase(appearance, dir = DIR_SOUTH) {
    const bodyEntry = BODY_TYPE_LIST.find(b => b.id === appearance.bodyType);
    const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
    const skinColor = (appearance.skinColor || 'light').replace(/ /g, '_');
    const headId = appearance.head || 'heads_human_male';
    const newKey = `${bodyKey}:${skinColor}:${headId}`;

    const cached = this._bases[dir];
    if (cached && cached.key === newKey) return cached.canvas;

    // Body-only canvas (reused by head category thumbnails)
    const bodyCanvas = document.createElement('canvas');
    bodyCanvas.width = 64; bodyCanvas.height = 64;
    const bodyCtx = bodyCanvas.getContext('2d');
    bodyCtx.imageSmoothingEnabled = false;

    const bodyOverride = BODY_ITEM_OVERRIDES[appearance.bodyType];
    const bodyItemId = bodyOverride ? bodyOverride.item : 'body';
    const bodyVariant = bodyOverride ? bodyOverride.variant : skinColor;
    await this._drawItemFrame(bodyCtx, bodyItemId, bodyKey, bodyVariant, dir);

    // Full base = body + head
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bodyCanvas, 0, 0);

    const headItem = findItemById(headId);
    if (headItem) {
      const headVariant = headItem.matchBodyColor ? skinColor : (headItem.variants?.[0] || skinColor);
      await this._drawItemFrame(ctx, headId, bodyKey, headVariant, dir);
    }

    this._bases[dir] = { canvas, key: newKey, bodyCanvas };
    return canvas;
  }

  getBodyOnlyCanvas(dir = DIR_SOUTH) {
    return this._bases[dir]?.bodyCanvas || null;
  }

  async renderThumbnail(itemId, variant, bodyKey, dir = DIR_SOUTH, minZPos = -Infinity) {
    const baseKey = this._bases[dir]?.key || '';
    const cacheKey = `${itemId}:${variant}:${dir}:${minZPos}:${baseKey}`;
    if (this._cache.has(cacheKey)) return this._cache.get(cacheKey);

    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    const base = this._bases[dir]?.canvas;
    if (base) ctx.drawImage(base, 0, 0);
    if (itemId) await this._drawItemFrame(ctx, itemId, bodyKey, variant, dir, minZPos);

    this._cache.set(cacheKey, canvas);
    return canvas;
  }

  async _drawItemFrame(ctx, itemId, bodyKey, variant, dir = DIR_SOUTH, minZPos = -Infinity) {
    const item = findItemById(itemId);
    if (!item) return;
    const safeVariant = String(variant).replace(/ /g, '_');
    const fileIndex = getFileIndex();

    const sorted = [...item.layers].sort((a, b) => a.zPos - b.zPos);
    for (const layer of sorted) {
      if (layer.zPos < minZPos) continue;
      let path = layer.bodyTypes[bodyKey];
      if (!path) {
        const keys = Object.keys(layer.bodyTypes);
        if (keys.length > 0) path = layer.bodyTypes[keys[0]];
      }
      if (!path || path.indexOf('${') >= 0) continue;
      path = path.replace(/\/$/, '');

      const fi = fileIndex ? fileIndex[path] : null;
      // Try idle first, then walk — using file index to find the correct path
      const resolved = LPCCompositor._resolvePathFromIndex(fi, path, safeVariant, 'idle', 'idle', layer)
        || LPCCompositor._resolvePathFromIndex(fi, path, safeVariant, 'walk', 'walk', layer);
      if (!resolved) continue;

      try {
        const img = await LPCCompositor._loadImage(`/lpc/spritesheets/${resolved.path}`);
        ctx.drawImage(img, 0, dir, 64, 64, 0, 0, 64, 64);
      } catch (e) { /* sprite load failed — skip silently */ }
    }
  }

  invalidate() {
    this._cache.clear();
    this._bases = {};
  }

  destroy() {
    this._cache.clear();
    this._bases = {};
  }
}

// Tab definitions
const TABS = [
  { id: 'body',     label: 'Body',     icon: '&#9899;' },
  { id: 'face',     label: 'Face',     icon: '&#9786;' },
  { id: 'hair',     label: 'Hair',     icon: '&#10047;' },
  { id: 'features', label: 'Features', icon: '&#9734;' },
  { id: 'extras',   label: 'Extras',   icon: '&#9733;' }
];

// Which categories appear in each tab
const TAB_SECTIONS = {
  body: ['body'],
  face: ['head', 'face', 'eyes', 'eyebrows'],
  hair: ['hair', 'hairext', 'beard'],
  features: ['ears', 'nose', 'facial'],
  extras: ['horns', 'tail', 'wings']
};

// Default appearance for new characters
const DEFAULT_APPEARANCE = {
  bodyType: 'human-male',
  skinColor: 'light',
  head: 'heads_human_male',
  face: null,
  eyes: null,
  eyeColor: 'blue',
  eyebrows: null,
  eyebrowColor: 'dark brown',
  hair: 'hair_bangs',
  hairColor: 'dark brown',
  hairext: null,
  beard: null,
  beardColor: 'dark brown',
  ears: null,
  nose: null,
  facial: null,
  facialVariant: null,
  horns: null,
  tail: null,
  wings: null
};

export default class CharacterCreator {
  constructor(options = {}) {
    this.onConfirm = options.onConfirm || (() => {});
    this.onCancel = options.onCancel || (() => {});
    this._getEquipment = options.getEquipment || (() => ({}));

    // Current selections — merge saved on top of defaults
    this.appearance = { ...DEFAULT_APPEARANCE };
    const saved = localStorage.getItem('trixel_lpc_appearance');
    if (saved) {
      try { Object.assign(this.appearance, JSON.parse(saved)); } catch (e) { /* ignore */ }
    }

    // Active tab
    this._activeTab = 'body';

    // Preview state
    this._previewCanvas = null;
    this._previewCtx = null;
    this._previewAnimFrame = 0;
    this._previewAnimTimer = null;
    this._previewAtlas = null;
    this._previewImg = null;
    this._previewDirty = false;
    this._previewDirection = 'south';
    this._previewAnim = 'walking-8-frames';
    this._showGear = false;

    // Drag-to-rotate state
    this._dragStartX = 0;
    this._dragging = false;

    // Thumbnail renderer for item previews
    this._thumbRenderer = new ThumbnailRenderer();
    this._thumbObserver = null;

    // DOM
    this.element = null;
    this._tabContent = null;
  }

  buildContent() {
    const el = document.createElement('div');
    el.className = 'cc-container';

    el.innerHTML = `
      <div class="cc-preview-section">
        <canvas class="cc-preview-canvas" width="192" height="192"></canvas>
        <div class="cc-actions">
          <button class="cc-btn cc-btn-cancel">Cancel</button>
          <button class="cc-btn cc-btn-confirm">Confirm</button>
          <button class="cc-btn cc-randomize-btn">Randomize</button>
          <label class="cc-gear-toggle">
            <input type="checkbox" class="cc-gear-checkbox">
            <span>Show Gear</span>
          </label>
          <div class="cc-anim-toggle">
            <button class="cc-anim-btn cc-anim-active" data-anim="walking-8-frames">Walk</button>
            <button class="cc-anim-btn" data-anim="breathing-idle">Idle</button>
            <button class="cc-anim-btn" data-anim="running-8-frames">Run</button>
          </div>
        </div>
      </div>
      <div class="cc-tabs"></div>
      <div class="cc-options-scroll">
        <div class="cc-tab-content"></div>
      </div>
      <div class="cc-credits">
        <div class="cc-credits-authors"></div>
        <div class="cc-credits-lpc">
          <a href="https://liberatedpixelcup.github.io/Universal-LPC-Spritesheet-Character-Generator/"
             target="_blank" rel="noopener">Liberated Pixel Cup Character Generator</a>
        </div>
      </div>
    `;

    this.element = el;
    this._tabContent = el.querySelector('.cc-tab-content');
    this._creditsAuthors = el.querySelector('.cc-credits-authors');

    // Preview canvas
    this._previewCanvas = el.querySelector('.cc-preview-canvas');
    this._previewCtx = this._previewCanvas.getContext('2d');
    this._previewCtx.imageSmoothingEnabled = false;

    // Build tabs
    this._buildTabs(el.querySelector('.cc-tabs'));

    // Buttons
    el.querySelector('.cc-btn-confirm').addEventListener('click', () => this._confirm());
    el.querySelector('.cc-btn-cancel').addEventListener('click', () => this._cancel());
    el.querySelector('.cc-randomize-btn').addEventListener('click', () => this._randomize());

    // Gear toggle
    el.querySelector('.cc-gear-checkbox').addEventListener('change', (e) => {
      this._showGear = e.target.checked;
      this._updatePreview();
    });

    // Animation toggle
    for (const btn of el.querySelectorAll('.cc-anim-btn')) {
      btn.addEventListener('click', () => {
        el.querySelector('.cc-anim-btn.cc-anim-active')?.classList.remove('cc-anim-active');
        btn.classList.add('cc-anim-active');
        this._previewAnim = btn.dataset.anim;
        this._previewAnimFrame = 0;
      });
    }

    // Drag-to-rotate on preview canvas
    this._initPreviewDrag();

    // Ensure manifest is loaded, then render
    loadManifest().then(() => {
      this._renderTab(this._activeTab);
      this._updatePreview();
    });

    return el;
  }

  // ── Tab bar ─────────────────────────────────────────────────────────

  _buildTabs(container) {
    for (const tab of TABS) {
      const btn = document.createElement('button');
      btn.className = 'cc-tab-btn' + (tab.id === this._activeTab ? ' cc-tab-active' : '');
      btn.innerHTML = `<span class="cc-tab-icon">${tab.icon}</span><span class="cc-tab-label">${tab.label}</span>`;
      btn.dataset.tab = tab.id;
      btn.addEventListener('click', () => {
        this._activeTab = tab.id;
        container.querySelectorAll('.cc-tab-btn').forEach(b => b.classList.remove('cc-tab-active'));
        btn.classList.add('cc-tab-active');
        this._renderTab(tab.id);
      });
      container.appendChild(btn);
    }
  }

  // ── Tab content rendering ─────────────────────────────────────────

  _renderTab(tabId) {
    if (!this._tabContent) return;
    this._tabContent.innerHTML = '';

    // Disconnect previous observers
    if (this._thumbObserver) {
      this._thumbObserver.disconnect();
      this._thumbObserver = null;
    }

    // Pre-render base thumbnails (south for most, north for tails/wings)
    this._thumbRenderer.renderBase(this.appearance, DIR_SOUTH);
    if (tabId === 'extras') {
      this._thumbRenderer.renderBase(this.appearance, DIR_NORTH);
    }

    const sections = TAB_SECTIONS[tabId] || [];
    for (const catKey of sections) {
      if (catKey === 'body') {
        this._renderBodySection();
      } else {
        this._renderCategorySection(catKey);
      }
    }
  }

  // ── Body tab (special: body type + skin color) ────────────────────

  _renderBodySection() {
    const tc = this._tabContent;

    // Body type — use thumbnail previews
    const bodySection = this._createSection('Body Type');
    const bodyRow = bodySection.querySelector('.cc-option-row');
    bodyRow.classList.add('cc-thumb-grid');
    for (const bt of BODY_TYPE_LIST) {
      const btn = this._createThumbnailBtn(null, bt.name, 'body', bt.id === this.appearance.bodyType);
      btn._thumbBodyType = bt; // store for lazy loading
      btn.addEventListener('click', () => {
        this.appearance.bodyType = bt.id;
        this._refreshSelection(bodyRow, btn);
        this._autoMatchBodyType(bt.bodyKey);
        this._thumbRenderer.invalidate();
        this._renderTab('body');
        this._updatePreview();
      });
      bodyRow.appendChild(btn);
    }
    tc.appendChild(bodySection);

    // Lazy-load body type thumbnails
    this._observeThumbnails(bodySection);

    // Skin color
    const skinSection = this._createSection('Skin Color');
    const skinRow = skinSection.querySelector('.cc-option-row');
    const skinVariants = getSkinVariants();
    for (const variant of skinVariants) {
      const swatch = this._createSwatch(
        SKIN_SWATCHES[variant] || '#888',
        variant,
        variant === this.appearance.skinColor
      );
      swatch.addEventListener('click', () => {
        this.appearance.skinColor = variant;
        this._thumbRenderer.invalidate();
        this._refreshSelection(skinRow, swatch);
        this._updatePreview();
      });
      skinRow.appendChild(swatch);
    }
    tc.appendChild(skinSection);
  }

  // ── Generic category section ──────────────────────────────────────

  _renderCategorySection(catKey) {
    const cat = CREATOR_CATEGORIES[catKey];
    if (!cat) return;

    const bodyEntry = BODY_TYPE_LIST.find(b => b.id === this.appearance.bodyType);
    const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
    const items = getItemsForCategory(catKey, bodyKey);
    if (items.length === 0) return;

    const section = this._createSection(cat.label);
    const tc = this._tabContent;

    // Item picker (with optional "None" button)
    const currentItemId = this._getAppearanceKey(catKey);
    const itemRow = section.querySelector('.cc-option-row');
    itemRow.classList.add('cc-thumb-grid');

    // "None" option
    const noneBtn = this._createThumbnailBtn(null, 'None', catKey, !currentItemId);
    noneBtn.addEventListener('click', () => {
      this._setAppearanceKey(catKey, null);
      this._refreshSelection(itemRow, noneBtn);
      const varRow = section.querySelector('.cc-variant-section');
      if (varRow) varRow.remove();
      this._updatePreview();
    });
    itemRow.appendChild(noneBtn);

    // Group items if category has groups
    if (cat.groups) {
      for (const group of cat.groups) {
        const groupItems = items.filter(i => group.filter(i.id));
        if (groupItems.length === 0) continue;

        const groupLabel = document.createElement('div');
        groupLabel.className = 'cc-group-label';
        groupLabel.textContent = group.name;
        itemRow.appendChild(groupLabel);

        for (const item of groupItems) {
          const btn = this._createThumbnailBtn(item, item.name, catKey, item.id === currentItemId);
          btn.addEventListener('click', () => {
            this._setAppearanceKey(catKey, item.id);
            this._refreshSelection(itemRow, btn);
            this._showVariants(section, catKey, item.id);
            this._updatePreview();
          });
          itemRow.appendChild(btn);
        }
      }
    } else {
      for (const item of items) {
        const btn = this._createThumbnailBtn(item, item.name, catKey, item.id === currentItemId);
        btn.addEventListener('click', () => {
          this._setAppearanceKey(catKey, item.id);
          this._refreshSelection(itemRow, btn);
          this._showVariants(section, catKey, item.id);
          this._updatePreview();
        });
        itemRow.appendChild(btn);
      }
    }

    tc.appendChild(section);

    // Lazy-load thumbnails via IntersectionObserver
    this._observeThumbnails(section);

    // Show variants for currently selected item
    if (currentItemId) {
      this._showVariants(section, catKey, currentItemId);
    }
  }

  // ── Variant picker (color swatches below item selection) ──────────

  _showVariants(section, catKey, itemId) {
    // Remove existing variant row
    const existing = section.querySelector('.cc-variant-section');
    if (existing) existing.remove();

    const cat = CREATOR_CATEGORIES[catKey];
    const variants = getVariantsForItem(itemId);
    if (variants.length <= 1) return; // No color choice needed

    const varSection = document.createElement('div');
    varSection.className = 'cc-variant-section';

    // Determine which swatch palette and appearance key to use
    let swatches, currentVariant, setVariant, label;

    if (cat.variantType === 'hair') {
      swatches = HAIR_SWATCHES;
      label = 'Color';
      if (catKey === 'hair' || catKey === 'hairext') {
        currentVariant = this.appearance.hairColor;
        setVariant = v => { this.appearance.hairColor = v; };
      } else if (catKey === 'beard') {
        currentVariant = this.appearance.beardColor || this.appearance.hairColor;
        setVariant = v => { this.appearance.beardColor = v; };
      } else if (catKey === 'eyebrows') {
        currentVariant = this.appearance.eyebrowColor || this.appearance.hairColor;
        setVariant = v => { this.appearance.eyebrowColor = v; };
      }
    } else if (cat.variantType === 'eye') {
      swatches = EYE_SWATCHES;
      label = 'Eye Color';
      currentVariant = this.appearance.eyeColor;
      setVariant = v => { this.appearance.eyeColor = v; };
    } else {
      // Auto-detect: check if variants look like skin colors, hair colors, or generic
      const variantKey = catKey + 'Variant'; // e.g., earsVariant, tailVariant
      const isSkinLike = variants.some(v => v === 'light' || v === 'amber' || v === 'olive');
      const isHairLike = variants.some(v => v === 'blonde' || v === 'ash' || v === 'sandy');

      if (isSkinLike) {
        swatches = SKIN_SWATCHES;
        label = 'Color';
        currentVariant = this.appearance.skinColor;
        setVariant = v => {
          this.appearance[variantKey] = v;
        };
        if (this.appearance[variantKey]) currentVariant = this.appearance[variantKey];
        // "Match Skin" reset button — clears override so it follows body skin color
        this._skinLikeResetKey = variantKey;
      } else if (isHairLike) {
        swatches = HAIR_SWATCHES;
        label = 'Color';
        currentVariant = this.appearance[variantKey] || this.appearance.hairColor;
        setVariant = v => { this.appearance[variantKey] = v; };
      } else {
        // Use general variant color swatches if most variants have known colors
        const knownCount = variants.filter(v => VARIANT_SWATCHES[v]).length;
        if (knownCount >= variants.length * 0.5) {
          swatches = VARIANT_SWATCHES;
          label = 'Color';
        } else {
          swatches = null;
          label = 'Variant';
        }
        currentVariant = this.appearance[variantKey] || null;
        setVariant = v => { this.appearance[variantKey] = v; };
      }
    }

    const labelEl = document.createElement('div');
    labelEl.className = 'cc-section-title';
    labelEl.textContent = label;
    varSection.appendChild(labelEl);

    const row = document.createElement('div');
    row.className = 'cc-option-row';

    if (swatches) {
      // Add "Match Skin" reset button for skin-like variants
      if (this._skinLikeResetKey) {
        const resetKey = this._skinLikeResetKey;
        const isActive = !this.appearance[resetKey]; // active when no override
        const resetBtn = this._createOptionBtn('Match Skin', isActive);
        resetBtn.addEventListener('click', () => {
          delete this.appearance[resetKey];
          this._thumbRenderer.invalidate();
          this._renderTab(this._activeTab);
          this._updatePreview();
        });
        row.appendChild(resetBtn);
        this._skinLikeResetKey = null;
      }
      for (const variant of variants) {
        const hex = swatches[variant];
        if (!hex) continue;
        const swatch = this._createSwatch(hex, variant, variant === currentVariant);
        swatch.addEventListener('click', () => {
          setVariant(variant);
          this._refreshSelection(row, swatch);
          // Color change affects thumbnails — invalidate and re-render tab
          this._thumbRenderer.invalidate();
          this._renderTab(this._activeTab);
          this._updatePreview();
        });
        row.appendChild(swatch);
      }
    } else {
      for (const variant of variants) {
        const name = variant.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const btn = this._createOptionBtn(name, variant === currentVariant);
        btn.addEventListener('click', () => {
          setVariant(variant);
          this._refreshSelection(row, btn);
          this._thumbRenderer.invalidate();
          this._renderTab(this._activeTab);
          this._updatePreview();
        });
        row.appendChild(btn);
      }
    }

    varSection.appendChild(row);
    section.appendChild(varSection);
  }

  // ── Appearance key mapping ────────────────────────────────────────

  _getAppearanceKey(catKey) {
    const map = {
      head: 'head', face: 'face', eyes: 'eyes', eyebrows: 'eyebrows',
      hair: 'hair', hairext: 'hairext', beard: 'beard', ears: 'ears',
      nose: 'nose', facial: 'facial', horns: 'horns', tail: 'tail', wings: 'wings'
    };
    return this.appearance[map[catKey]] || null;
  }

  _setAppearanceKey(catKey, value) {
    const map = {
      head: 'head', face: 'face', eyes: 'eyes', eyebrows: 'eyebrows',
      hair: 'hair', hairext: 'hairext', beard: 'beard', ears: 'ears',
      nose: 'nose', facial: 'facial', horns: 'horns', tail: 'tail', wings: 'wings'
    };
    const key = map[catKey];
    if (key) this.appearance[key] = value;
  }

  _getVariantKey(catKey) {
    return this.appearance[catKey + 'Variant'] || null;
  }

  _setVariantKey(catKey, value) {
    this.appearance[catKey + 'Variant'] = value;
  }

  // ── Auto-match head & clear incompatible items on body type change ─

  _autoMatchBodyType(bodyKey) {
    // 1. Auto-match head to body type
    const bodyType = this.appearance.bodyType;
    if (bodyType && bodyType.startsWith('skeleton')) {
      this.appearance.head = 'heads_skeleton';
    } else if (bodyType && bodyType.startsWith('zombie')) {
      this.appearance.head = 'heads_zombie';
    } else {
      const head = this.appearance.head;
      if (head && (head.startsWith('heads_human') || head === 'heads_skeleton' || head === 'heads_zombie')) {
        const headMap = {
          'male': 'heads_human_male',
          'female': 'heads_human_female',
          'muscular': 'heads_human_male',
          'teen': 'heads_human_male',
          'child': 'heads_human_child'
        };
        if (headMap[bodyKey]) {
          this.appearance.head = headMap[bodyKey];
        }
      }
    }

    // 2. Clear any other slots whose current item lacks this body type
    const slotsToCheck = [
      'hair', 'hairext', 'beard', 'eyes', 'eyebrows',
      'ears', 'nose', 'facial', 'horns', 'tail', 'wings', 'face'
    ];
    for (const slot of slotsToCheck) {
      const itemId = this.appearance[slot];
      if (itemId && !isItemCompatibleWithBody(itemId, bodyKey)) {
        this.appearance[slot] = null;
      }
    }
  }

  // ── DOM helpers ───────────────────────────────────────────────────

  _createSection(title) {
    const section = document.createElement('div');
    section.className = 'cc-section';
    section.innerHTML = `
      <div class="cc-section-title">${title}</div>
      <div class="cc-option-row"></div>
    `;
    return section;
  }

  _createOptionBtn(label, selected) {
    const btn = document.createElement('button');
    btn.className = 'cc-option-btn' + (selected ? ' cc-selected' : '');
    btn.textContent = label;
    return btn;
  }

  _createSwatch(hex, title, selected) {
    const swatch = document.createElement('button');
    swatch.className = 'cc-swatch' + (selected ? ' cc-selected' : '');
    swatch.style.backgroundColor = hex;
    swatch.title = title;
    return swatch;
  }

  _refreshSelection(container, selectedBtn) {
    container.querySelectorAll('.cc-selected').forEach(el => el.classList.remove('cc-selected'));
    selectedBtn.classList.add('cc-selected');
  }

  // ── Thumbnail previews ─────────────────────────────────────────────

  _createThumbnailBtn(item, label, catKey, selected) {
    const btn = document.createElement('button');
    btn.className = 'cc-thumb-btn' + (selected ? ' cc-selected' : '');

    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    canvas.className = 'cc-thumb-canvas';
    btn.appendChild(canvas);

    const span = document.createElement('span');
    span.className = 'cc-thumb-label';
    span.textContent = label;
    btn.appendChild(span);

    // Store data for lazy loading
    btn._thumbCanvas = canvas;
    btn._thumbItemId = item ? item.id : null;
    btn._thumbCatKey = catKey;

    return btn;
  }

  _observeThumbnails(section) {
    if (!this._thumbObserver) {
      this._thumbObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this._thumbObserver.unobserve(entry.target);
          this._loadThumbnail(entry.target);
        }
      }, {
        root: this.element.querySelector('.cc-options-scroll'),
        rootMargin: '100px 0px'
      });
    }

    section.querySelectorAll('.cc-thumb-btn').forEach(btn => {
      this._thumbObserver.observe(btn);
    });
  }

  async _loadThumbnail(btn) {
    const canvas = btn._thumbCanvas;
    if (!canvas) return;

    const catKey = btn._thumbCatKey;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 64, 64);
    // Fill with dark bg to prevent semi-transparent edge halo artifacts
    ctx.fillStyle = 'rgba(8, 12, 24, 0.6)';
    ctx.fillRect(0, 0, 64, 64);

    try {
      // Body type thumbnails — render each body type with its own base
      if (btn._thumbBodyType) {
        const bt = btn._thumbBodyType;
        const skinColor = (this.appearance.skinColor || 'light').replace(/ /g, '_');
        const bodyOverride = BODY_ITEM_OVERRIDES[bt.id];
        const bodyItemId = bodyOverride ? bodyOverride.item : 'body';
        const bodyVariant = bodyOverride ? bodyOverride.variant : skinColor;
        await this._thumbRenderer._drawItemFrame(ctx, bodyItemId, bt.bodyKey, bodyVariant, DIR_SOUTH);
        const headMap = {
          'skeleton-male': 'heads_skeleton', 'zombie-male': 'heads_zombie',
          'human-child': 'heads_human_child', 'human-female': 'heads_human_female'
        };
        const headId = headMap[bt.id] || 'heads_human_male';
        const headItem = findItemById(headId);
        if (headItem) {
          const headVariant = headItem.matchBodyColor ? skinColor : (headItem.variants?.[0] || skinColor);
          await this._thumbRenderer._drawItemFrame(ctx, headId, bt.bodyKey, headVariant, DIR_SOUTH);
        }
        return;
      }

      // Determine direction and layer filtering per category
      const isBackView = catKey === 'tail' || catKey === 'wings';
      const dir = isBackView ? DIR_NORTH : DIR_SOUTH;
      // For front-facing items, skip bg layers (zPos < body) that would be hidden behind body
      const minZPos = isBackView ? -Infinity : BODY_ZPOS;

      const itemId = btn._thumbItemId;
      const bodyEntry = BODY_TYPE_LIST.find(b => b.id === this.appearance.bodyType);
      const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
      const variant = this._getPreviewVariant(catKey, itemId);

      await this._thumbRenderer.renderBase(this.appearance, dir);

      // Head category: use body-only base so candidate head isn't drawn over existing head
      // (but "None" keeps the full base with current head — no decapitation!)
      if (catKey === 'head' && itemId) {
        const bodyOnly = this._thumbRenderer.getBodyOnlyCanvas(dir);
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = 64; tmpCanvas.height = 64;
        const tmpCtx = tmpCanvas.getContext('2d');
        tmpCtx.imageSmoothingEnabled = false;
        if (bodyOnly) tmpCtx.drawImage(bodyOnly, 0, 0);
        await this._thumbRenderer._drawItemFrame(tmpCtx, itemId, bodyKey, variant, dir, minZPos);
        const crop = THUMB_CROPS[catKey];
        ctx.drawImage(tmpCanvas, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, 64, 64);
        return;
      }

      const result = await this._thumbRenderer.renderThumbnail(itemId, variant, bodyKey, dir, minZPos);
      if (result) {
        const crop = THUMB_CROPS[catKey];
        if (crop) {
          ctx.drawImage(result, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, 64, 64);
        } else {
          ctx.drawImage(result, 0, 0);
        }
      }
    } catch (e) { /* graceful fallback — canvas stays blank */ }
  }

  _getPreviewVariant(catKey, itemId) {
    const cat = CREATOR_CATEGORIES[catKey];
    if (!cat) return 'default';

    if (cat.variantType === 'hair') {
      if (catKey === 'hair' || catKey === 'hairext') return this.appearance.hairColor || 'dark brown';
      if (catKey === 'beard') return this.appearance.beardColor || this.appearance.hairColor || 'dark brown';
      if (catKey === 'eyebrows') return this.appearance.eyebrowColor || this.appearance.hairColor || 'dark brown';
    }
    if (cat.variantType === 'eye') return this.appearance.eyeColor || 'blue';

    if (!itemId) return this.appearance.skinColor || 'light';

    const item = findItemById(itemId);
    if (item && item.matchBodyColor) return this.appearance.skinColor || 'light';

    const variantKey = catKey + 'Variant';
    if (this.appearance[variantKey]) return this.appearance[variantKey];

    if (item && item.variants && item.variants.length > 0) return item.variants[0];
    return 'default';
  }

  // ── Credits ──────────────────────────────────────────────────────

  _updateCredits() {
    if (!this._creditsAuthors) return;

    const SLOT_LABELS = {
      body: 'Body', head: 'Head', face: 'Face', eyes: 'Eyes',
      eyebrows: 'Eyebrows', hair: 'Hair', hairext: 'Hair Ext.',
      beard: 'Beard', ears: 'Ears', nose: 'Nose', facial: 'Facial',
      horns: 'Horns', tail: 'Tail', wings: 'Wings'
    };

    const slots = [
      'head', 'face', 'eyes', 'eyebrows', 'hair', 'hairext',
      'beard', 'ears', 'nose', 'facial', 'horns', 'tail', 'wings'
    ];

    // Map body type to manifest body item ID
    const bodyType = this.appearance.bodyType;
    const bodyItemId = bodyType === 'skeleton-male' ? 'body_skeleton'
      : bodyType === 'zombie-male' ? 'body_zombie' : 'body';

    const getAuthors = (item) => {
      if (!item || !item.credits) return null;
      const authors = new Set();
      for (const credit of item.credits) {
        for (const author of credit.authors) authors.add(author);
      }
      return authors.size > 0 ? [...authors].sort((a, b) => a.localeCompare(b)) : null;
    };

    // Build per-slot entries
    const entries = [];
    const bodyAuthors = getAuthors(findItemById(bodyItemId));
    if (bodyAuthors) entries.push({ label: 'Body', authors: bodyAuthors });

    for (const key of slots) {
      const itemId = this.appearance[key];
      if (!itemId) continue;
      const authors = getAuthors(findItemById(itemId));
      if (authors) entries.push({ label: SLOT_LABELS[key], authors });
    }

    // Render as list
    this._creditsAuthors.innerHTML = '';
    for (const entry of entries) {
      const line = document.createElement('div');
      line.className = 'cc-credit-line';
      line.innerHTML = `<span class="cc-credit-label">${entry.label}:</span> ${entry.authors.join(', ')}`;
      this._creditsAuthors.appendChild(line);
    }
  }

  // ── Preview ───────────────────────────────────────────────────────

  async _updatePreview() {
    this._updateCredits();
    const equipment = this._showGear ? this._getEquipment() : {};
    const layers = buildLayerStack(this.appearance, equipment);
    if (layers.length === 0) return;

    try {
      const atlas = await LPCCompositor.compose(layers, ['walking-8-frames', 'breathing-idle', 'running-8-frames']);
      this._previewAtlas = atlas;

      if (!this._previewAnimTimer) {
        this._startPreviewAnimation();
      }
    } catch (e) {
      this._drawPlaceholder();
    }
  }

  _startPreviewAnimation() {
    this._previewAnimFrame = 0;
    const animate = () => {
      this._drawPreviewFrame();
      this._previewAnimFrame = (this._previewAnimFrame + 1) % 9;
      this._previewAnimTimer = setTimeout(() => requestAnimationFrame(animate), 120);
    };
    animate();
  }

  _drawPreviewFrame() {
    const ctx = this._previewCtx;
    const canvas = this._previewCanvas;
    if (!ctx || !this._previewAtlas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const dir = this._previewDirection;
    const anim = this._previewAnim;
    const frameCount = this._previewAtlas.getFrameCount(anim, dir);
    if (frameCount === 0) return;

    const frameData = this._previewAtlas.getAnimationFrame(
      anim, dir, this._previewAnimFrame % frameCount
    );

    if (frameData && this._previewAtlas.url) {
      if (!this._previewImg || this._previewImg.src !== this._previewAtlas.url) {
        this._previewImg = new Image();
        this._previewImg.src = this._previewAtlas.url;
      }

      if (this._previewImg.complete) {
        const ts = frameData.tileSize || 64;
        // Oversize tiles (e.g. 192px for bows) center the 64px character.
        // Extract the center 64px so the character stays the same size.
        const offset = (ts - 64) / 2;
        ctx.drawImage(
          this._previewImg,
          frameData.x + offset, frameData.y + offset, 64, 64,
          0, 0, 192, 192
        );
      }
    }
  }

  _drawPlaceholder() {
    const ctx = this._previewCtx;
    const canvas = this._previewCanvas;
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(32, 32, 128, 128);
    ctx.fillStyle = '#888';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Loading...', 96, 100);
  }

  // ── Drag-to-rotate ──────────────────────────────────────────────

  _initPreviewDrag() {
    const canvas = this._previewCanvas;
    const DIRECTIONS = ['south', 'south-west', 'west', 'north-west', 'north', 'north-east', 'east', 'south-east'];
    const DRAG_THRESHOLD = 30; // px per direction step

    const onMouseDown = (e) => { e.preventDefault(); onStart(e.clientX); };
    const onTouchStart = (e) => { e.preventDefault(); onStart(e.touches[0].clientX); };

    const onStart = (x) => {
      this._dragging = true;
      this._dragStartX = x;
      this._dragDirIndex = DIRECTIONS.indexOf(this._previewDirection);
    };

    const onMouseMove = (e) => { onMove(e.clientX); };
    const onTouchMove = (e) => { if (this._dragging) onMove(e.touches[0].clientX); };

    const onMove = (x) => {
      if (!this._dragging) return;
      const delta = x - this._dragStartX;
      const steps = Math.round(delta / DRAG_THRESHOLD);
      if (steps === 0) return;
      const newIndex = ((this._dragDirIndex - steps) % 8 + 8) % 8;
      if (DIRECTIONS[newIndex] !== this._previewDirection) {
        this._previewDirection = DIRECTIONS[newIndex];
      }
    };

    const onEnd = () => { this._dragging = false; };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onEnd);

    this._dragCleanup = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
    };
  }

  // ── Actions ───────────────────────────────────────────────────────

  _confirm() {
    localStorage.setItem('trixel_lpc_appearance', JSON.stringify(this.appearance));
    // Clear legacy character selection — LPC appearance takes priority on reload
    localStorage.removeItem('trixel_selected_character');
    this.onConfirm(this.appearance);
  }

  _cancel() {
    this.onCancel();
  }

  _randomize() {
    // Keep the current body type so we randomize features, not switch to skeleton/zombie
    const randomAppearance = generateRandomAppearance({
      bodyType: this.appearance.bodyType,
      seed: Math.floor(Math.random() * 999999)
    });
    Object.assign(this.appearance, randomAppearance);
    if (this._thumbRenderer) this._thumbRenderer.invalidate();
    this._renderTab(this._activeTab);
    this._updatePreview();
  }

  destroy() {
    if (this._previewAnimTimer) {
      clearTimeout(this._previewAnimTimer);
      this._previewAnimTimer = null;
    }
    if (this._dragCleanup) {
      this._dragCleanup();
      this._dragCleanup = null;
    }
    if (this._thumbObserver) {
      this._thumbObserver.disconnect();
      this._thumbObserver = null;
    }
    this._thumbRenderer.destroy();
    this._previewAtlas = null;
    this._previewImg = null;
    this.element = null;
    this._tabContent = null;
  }
}
